/* istanbul ignore file */
import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Box, ChakraProvider, ColorModeScript, createStandaloneToast } from '@chakra-ui/react';
import { createTheme, LeasingJourneyTheme } from '@common/ui';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from 'App';
import { EntrataSession } from 'auth/EntrataSession';
import { ViewerTranslationsProvider } from 'providers/ViewerTranslationsProvider';
import { setIsAuthenticated } from 'state/slices/authSlice';
import { setBrandingMedia } from 'state/slices/brandingSlice';
import { setLocale } from 'state/slices/localeSlice';
import { RootState } from 'state/store';
import { useDispatch, useSelector } from 'state/storeHooks';
import { CustomDevTools } from 'test-utils/CustomDevTools';

import { Loader } from 'components/Loader';
import { NotificationListener } from 'components/notifications/NotificationListener';
import { readStoredSdkTheme } from 'components/payment-handoff/launchContext';
import { PaymentHandoffBoundary } from 'components/payment-handoff/PaymentHandoffBoundary';
import { useApplicantJourneyTracking } from 'hooks/useApplicantJourneyTracking';
import { useInitializeLJApplication } from 'hooks/useInitializeLJApplication';
import { useLeasingJourneySDK } from 'hooks/useLeasingJourneySDK';
import { usePropertyLocale } from 'hooks/usePropertyLocale';
import { useLocale } from 'hooks/utils/useLocale';
import { getBrandingColors, getBrandingMedia } from 'services/branding';
import { fetchViewerLaunchContext } from 'services/viewerLaunchContext';
import { setFormattingDateLocale } from 'utils/formatting/formattingLocale';
import { validateLocale } from 'utils/locale';
import { startLaunchContextHydration } from 'utils/viewerLaunchContextHydration';
import {
  applyLaunchContextToSessionStorage,
  clearLaunchSelectionFromSession,
  readBrandingParamsFromSessionStorage,
  resolveEntrataRedirectLink,
} from 'utils/viewerLaunchContextSession';

import '@entrata/i18n';

// sync translation data from global var
i18nUtils.syncTranslations('__ENTRATA_APPLICATIONS__');

const { ToastContainer } = createStandaloneToast();

const ViewerApp = ({ paymentRestored = false }: { paymentRestored?: boolean }) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isNewAccount = useSelector((state: RootState) => state.auth.isNewAccount);
  const applicant = useSelector((state: RootState) => state.application.applicant);
  const form = useSelector((state: RootState) => state.application.form);
  const responseId = useSelector((state: RootState) => {
    const applicationStore = state.application;
    let resId = applicationStore.activeResponse?.responseId;
    if (!resId) {
      resId = applicationStore?.applicant?.currentResponse?.responseId;
    }
    return resId;
  });
  const response = useSelector((state: RootState) => state.response.value);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewMode = location.pathname.includes('/preview/');

  const [loading, setLoading] = useState(false);
  const [brandingColors, setBrandingColors] = useState<any | null>(null);
  const [sdkTheme, setSdkTheme] = useState<LeasingJourneyTheme | null>(() =>
    readStoredSdkTheme(paymentRestored),
  );

  const locale = useLocale();
  const { isReady: isPropertyLocaleReady } = usePropertyLocale({
    orgId: applicant?.org?.id,
    propertyId: applicant?.propertyId,
  });

  const initializeLJApplication = useInitializeLJApplication();
  const { containerRef, isEmbedded, sendAuthSuccess } = useLeasingJourneySDK();
  useApplicantJourneyTracking();
  const hasInitializedAppRef = React.useRef(paymentRestored);
  const sentAuthSuccessRef = React.useRef(false);
  const launchHydrationStartedRef = React.useRef(false);
  // Sync locale to @entrata/i18n and document.lang for date, currency, and
  // number formatting. Runs whenever the effective locale changes.
  useEffect(() => {
    if (locale) {
      setFormattingDateLocale(locale);
      if (typeof document?.documentElement?.setAttribute === 'function') {
        document.documentElement.lang = locale;
      }
      if (typeof i18nUtils?.setLocale === 'function') {
        i18nUtils.setLocale(locale);
      }
    }
  }, [locale]);

  useEffect(() => {
    EntrataSession.setAppPath(
      `${window.location.protocol}//${window.location.host}${window.location.pathname}${window.location.search}`,
    );
    const storedBrandingColorsString = sessionStorage.getItem('brandingColors');
    const storedBrandingMediaString = sessionStorage.getItem('brandingMedia');

    const storedBrandingColors = JSON.parse(storedBrandingColorsString as string);
    const storedBrandingMedia = JSON.parse(storedBrandingMediaString as string);

    if (storedBrandingColors) {
      setBrandingColors(storedBrandingColors);
    }
    if (storedBrandingMedia) {
      dispatch(setBrandingMedia(storedBrandingMedia));
    }

    // Persist URL launch params immediately; async fetch may finish later.
    resolveEntrataRedirectLink(new URLSearchParams(window.location.search));

    if (paymentRestored) return;

    // Hydration is intentionally one-shot for the current document. Route
    // changes reuse the resolved launch context and must not clear its theme.
    if (launchHydrationStartedRef.current) return;
    launchHydrationStartedRef.current = true;

    startLaunchContextHydration(async () => {
      let shouldRefreshBranding = false;
      const launchContextId = new URLSearchParams(window.location.search).get('launchContextId');
      const preservePaymentTheme = !launchContextId && readStoredSdkTheme() !== null;

      // A new/legacy launch must not inherit a theme from a previous SDK
      // launch while the current launch context is being resolved.
      if (!preservePaymentTheme) {
        sessionStorage.removeItem('leasingJourneySdkTheme');
        sessionStorage.removeItem('leasingJourneySdkThemeLaunchContextId');
        setSdkTheme(null);
      }

      try {
        // eslint-disable-next-line no-console
        console.log('[viewer-launch] AppWrapper bootstrap — hydrating launch context');

        const launch = launchContextId ? await fetchViewerLaunchContext(launchContextId) : null;
        if (launch && launchContextId) {
          shouldRefreshBranding = true;
          sessionStorage.removeItem('brandingColors');
          sessionStorage.removeItem('brandingMedia');
          setBrandingColors(null);
          dispatch(setBrandingMedia(null));
          applyLaunchContextToSessionStorage(launch);
          if (launch.theme) {
            sessionStorage.setItem('leasingJourneySdkTheme', JSON.stringify(launch.theme));
            sessionStorage.setItem('leasingJourneySdkThemeLaunchContextId', launchContextId);
            setSdkTheme(launch.theme);
          } else {
            sessionStorage.removeItem('leasingJourneySdkTheme');
            sessionStorage.removeItem('leasingJourneySdkThemeLaunchContextId');
            setSdkTheme(null);
          }
          if (launch.locale) {
            // eslint-disable-next-line no-console
            console.log(`[viewer-launch] applying locale from launch: ${launch.locale}`);
            dispatch(setLocale(validateLocale(String(launch.locale))));
          }
        } else if (launchContextId) {
          shouldRefreshBranding = true;
          clearLaunchSelectionFromSession();
          sessionStorage.removeItem('brandingColors');
          sessionStorage.removeItem('brandingMedia');
          sessionStorage.removeItem('queryParams');
          sessionStorage.removeItem('entrataRedirectLink');
          setBrandingColors(null);
          dispatch(setBrandingMedia(null));
          // eslint-disable-next-line no-console
          console.warn(
            `[viewer-launch] no launch context found; cleared stored branding — launchContextId=${launchContextId}`,
          );
        } else {
          // eslint-disable-next-line no-console
          console.log('[viewer-launch] AppWrapper — no launch context; URL/session fallback only');
        }
      } catch (error) {
        if (launchContextId) {
          shouldRefreshBranding = true;
          clearLaunchSelectionFromSession();
          sessionStorage.removeItem('brandingColors');
          sessionStorage.removeItem('brandingMedia');
          sessionStorage.removeItem('queryParams');
          sessionStorage.removeItem('entrataRedirectLink');
          setBrandingColors(null);
          dispatch(setBrandingMedia(null));
        }
        // eslint-disable-next-line no-console
        console.log('[viewer-launch] AppWrapper launch hydrate error', error);
        /* Launch context absent or network error — branding keys may be missing */
        if (!preservePaymentTheme) {
          sessionStorage.removeItem('leasingJourneySdkTheme');
          sessionStorage.removeItem('leasingJourneySdkThemeLaunchContextId');
          setSdkTheme(null);
        }
      }

      if (shouldRefreshBranding || !storedBrandingColors || !storedBrandingMedia) {
        const queryParams = readBrandingParamsFromSessionStorage();

        if (queryParams) {
          // eslint-disable-next-line no-console
          console.log(
            `[viewer-launch] fetching branding — orgId=${queryParams.orgId}, websiteId=${queryParams.websiteId}`,
          );
          setLoading(true);
          const brandParams = {
            websiteId: Number(queryParams.websiteId),
            orgId: Number(queryParams.orgId),
          };

          try {
            const [responseColors, responseMedia] = await Promise.all([
              getBrandingColors(brandParams),
              getBrandingMedia(brandParams),
            ]);

            sessionStorage.setItem('brandingColors', JSON.stringify(responseColors.data));
            sessionStorage.setItem('brandingMedia', JSON.stringify(responseMedia.data));
            setBrandingColors(responseColors.data);
            dispatch(setBrandingMedia(responseMedia.data));
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error fetching data:', error);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.pathname]);

  useEffect(() => {
    async function initializeApp() {
      setLoading(true);
      if (responseId) {
        await initializeLJApplication({ form, applicant, responseId });
      }
      setLoading(false);
    }

    if (EntrataSession.sessionExpired) {
      dispatch(setIsAuthenticated(false));
      return;
    }

    if (
      isAuthenticated &&
      !isNewAccount &&
      !isPreviewMode &&
      isPropertyLocaleReady &&
      !hasInitializedAppRef.current
    ) {
      hasInitializedAppRef.current = true;
      initializeApp();
    }

    if (location.pathname === '/' && applicant.org?.id && applicant.propertyId) {
      navigate(`/${applicant.org.id}/${applicant.propertyId}/entry`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applicant,
    dispatch,
    form,
    initializeLJApplication,
    isAuthenticated,
    isNewAccount,
    isPreviewMode,
    isPropertyLocaleReady,
    location.pathname,
    navigate,
    responseId,
  ]);

  useEffect(() => {
    if (!isEmbedded) {
      return;
    }

    if (!isAuthenticated) {
      sentAuthSuccessRef.current = false;
      return;
    }

    if (sentAuthSuccessRef.current) {
      return;
    }

    const firstName = String(response?.applicant_name_first || '');
    const lastName = String(response?.applicant_name_last || '');
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const email = String(applicant?.email || response?.applicant_email_address || '');

    sendAuthSuccess({
      name: fullName,
      email,
    });
    sentAuthSuccessRef.current = true;
  }, [applicant?.email, isAuthenticated, isEmbedded, response, sendAuthSuccess]);

  const theme = useMemo(() => createTheme(brandingColors, sdkTheme), [brandingColors, sdkTheme]);

  return (
    <>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <ChakraProvider theme={theme} cssVarsRoot="body">
        <Box ref={containerRef} h={isEmbedded && !isPreviewMode ? 'auto' : '100%'}>
          <NotificationListener />
          <Routes>
            {loading ? (
              <Route path="/*" element={<Loader />} />
            ) : (
              <Route
                path="/*"
                element={
                  <ViewerTranslationsProvider localeCode={locale}>
                    <App />
                  </ViewerTranslationsProvider>
                }
              />
            )}
          </Routes>
          <ToastContainer />
          <ReactQueryDevtools initialIsOpen={false} />
          <CustomDevTools />
        </Box>
      </ChakraProvider>
    </>
  );
};

const AppWrapper = () => (
  <PaymentHandoffBoundary>
    {(restored) => <ViewerApp paymentRestored={restored} />}
  </PaymentHandoffBoundary>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

export { AppWrapper, queryClient };
